import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineTokens,
  defineSemanticTokens,
  defineRecipe,
  defineSlotRecipe
} from "@chakra-ui/react"

import type { ColorPalette } from "@chakra-ui/react/styled-system"

//
// TOKENS
//
const tokens = defineTokens({
  colors: {
    blue: {
      600: { value: "#185FF6" },
    },

    gray: {
      50: { value: "#F8FAFC" },
      100: { value: "#F1F5F9" },
      200: { value: "#E2E8F0" },
      300: { value: "#CBD5E1" },
      400: { value: "#94A3B8" },
      500: { value: "#64748B" },
      600: { value: "#475569" },
      700: { value: "#334155" },
      800: { value: "#1E293B" },
      900: { value: "#0F172A" },
    },

    secondary: {
      50: { value: "#F8FBFF" },
      100: { value: "#F0F5FE" },
      200: { value: "#D8E6FD" },
      300: { value: "#B6D1FB" },
      400: { value: "#86B2F9" },
      500: { value: "#5B97F7" },
      600: { value: "#3B7BF4" },
      700: { value: "#2563eb" },
      800: { value: "#1D4ED8" },
      900: { value: "#1E40AF" },
    },

    orange: {
      600: { value: "#FD3704" },
    },

    gold: {
      100: { value: "#FFFBEB" },
      300: { value: "#FFEEB9" },
      500: { value: "#FCE39B" },
      900: { value: "#876600" },
    },
  },

  fonts: {
    body: {
      value:
        'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto',
    },
    heading: {
      value:
        'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto',
    },
  },
})

//
// SEMANTIC TOKENS
//
const semanticTokens = defineSemanticTokens({
  colors: {
    "bg.app": {
      value: {
        base: "gray.50",
        _dark: "gray.900",
      },
    },

    "text.primary": {
      value: {
        base: "gray.800",
        _dark: "gray.100",
      },
    },

    "text.secondary": {
      value: {
        base: "gray.500",
        _dark: "gray.400",
      },
    },

    "border.default": {
      value: {
        base: "gray.200",
        _dark: "gray.700",
      },
    },

    "button.secondary.bg": {
      value: {
        base: "gray.100",
        _dark: "gray.200",
      },
    },
  },
})

//
// BUTTON RECIPE
//

const subtleVariant = (palette: ColorPalette) => ({
  variant: "subtle",
  colorPalette: palette,
  css: {
    bg: `${palette}.100`,
    color: `${palette}.800`,

    _hover: {
      bg: `${palette}.300`,
    },

    _active: {
      bg: `${palette}.400`,
    },
  },
})

const solidVariant = (palette: ColorPalette) => ({
  variant: "solid",
  colorPalette: palette,
  css: {
    bg: `${palette}.600`,
    color: "white",

    _hover: {
      bg: `${palette}.700`,
    },

    _active: {
      bg: `${palette}.800`,
    },
  },
})

const buttonRecipe = defineRecipe({
  base: {
    rounded: "lg",
    fontWeight: "medium",
    borderWidth: "0",
    cursor: "pointer",

    _disabled: {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },

  compoundVariants: [
    subtleVariant("gray"),
    subtleVariant("blue"),
    subtleVariant("secondary" as unknown as ColorPalette),
    subtleVariant("orange"),
    subtleVariant("gold" as unknown as ColorPalette),

    solidVariant("gray"),
    solidVariant("blue"),
    solidVariant("secondary" as unknown as ColorPalette),
    solidVariant("orange"),
    solidVariant("gold" as unknown as ColorPalette),
  ],
})

//
// TABS
//
const tabsRecipe = defineSlotRecipe({
  slots: ["root", "list", "trigger", "indicator", "content"],

  base: {
    root: {},

    list: {
      gap: "1",
    },

    trigger: {
      px: "3",
      py: "1.5",
      fontWeight: "bold",
      color: "gray.900",
      transition: "all 0.2s",
      borderRadius: "full",
      bg: "white",
      shadow: "xs",

      _hover: {
        color: "text.primary",
      },

      _selected: {
        color: "text.primary",
      },
    },

    indicator: {
      transition: "all 0.2s",
      borderRadius: "full"
    },

    content: {
      mt: "3",
    },
  },

  variants: {
    variant: {
      plain: {
        list: {
          gap: 2,
          bg: "transparent",
          borderBottom: "0px",
        },

        trigger: {
          borderRadius: "full",
          borderBottom: "0px",

          _selected: {
            bg: "blue.600",
            color: "white",

            _hover: {
              color: "white"
            }
          },
        },

        indicator: {

        },
      },
      line: {
        trigger: {
          borderRadius: "unset",
          border: "0px",
          borderColor: "transparent",
          shadow: "unset"
        },

        indicator: {
          borderRadius: "unset",
          border: "0px",
          borderColor: "transparent",
          shadow: "unset"
        },
      }
    }
  }
})

//
// FINAL CONFIG
//
const config = defineConfig({
  theme: {
    tokens,
    semanticTokens,

    recipes: {
      button: buttonRecipe,
    },

    slotRecipes: {

      field: {
        slots: ['label'],
        base: {
          label: {
            fontWeight: "semibold",
            fontSize: "sm",
          },
        },
      },
      tabs: tabsRecipe,
    },
  },
})

//
// SYSTEM
//
export const system = createSystem(defaultConfig, config)
